// frontend/src/pages/CRManagement/CRDetailPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Card, Form, Input, Select, Button, DatePicker, InputNumber,
    Row, Col, Typography, Table, Tag, Popconfirm, Spin, Alert, Space,
    Divider, Modal, Result, Skeleton, Tooltip
} from 'antd';
import {
    PlusOutlined, RollbackOutlined, DeleteOutlined, EditOutlined,
    FileTextOutlined, LinkOutlined, ClockCircleOutlined, SaveOutlined,
    CloseOutlined, CheckOutlined, CopyOutlined, ExclamationCircleOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../utils/api';
import { useNotificationApi } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import {
    getCRById, updateCR, deleteCR,
    linkTickets, unlinkTicket,
    addTimeEntry, updateTimeEntry, deleteTimeEntry,
    getAvailableTickets
} from '../../utils/crService';
import './CRForm.css';
import './CRDetailPage.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { confirm } = Modal;

const DATE_FORMAT = 'DD/MM/YYYY';

// ============================================================
// CONSTANTS
// ============================================================

const ACTIVITY_TYPES = [
    'Analysis','Bug Fixes','Data Patching','Deployment','Development',
    'Documentation','Final Acceptance Test','Infra Setup & Configuration',
    'Meeting','Patch Upgrade','Review','Security Hardening',
    'System Integration Test','Support','Testing','User Acceptance Test',
    'URS','Other'
];

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);
const AMPM = ['AM', 'PM'];

// ============================================================
// HELPERS
// ============================================================

const buildTimeString = (hour, minute, ampm) => {
    if (hour == null || minute == null || !ampm) return null;
    let h = parseInt(hour);
    if (ampm === 'PM' && h !== 12) h += 12;
    if (ampm === 'AM' && h === 12) h = 0;
    return `${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
};

const parseTimeString = (timeStr) => {
    if (!timeStr) return { hour: undefined, minute: undefined, ampm: undefined };
    const m = dayjs(timeStr, 'HH:mm:ss');
    if (!m.isValid()) return { hour: undefined, minute: undefined, ampm: undefined };
    const h = m.hour();
    return {
        hour: h % 12 || 12,
        minute: m.minute(),
        ampm: h >= 12 ? 'PM' : 'AM'
    };
};

const emptyEntryForm = () => ({
    entryDate: null,
    activityType: undefined,
    startHour: undefined,
    startMinute: undefined,
    startAmPm: undefined,
    endHour: undefined,
    endMinute: undefined,
    endAmPm: undefined,
    duration: null,
    resources: [],
    description: ''
});

// ============================================================
// COMPONENT
// ============================================================

const CRDetailPage = () => {
    const navigate = useNavigate();
    const { id: crId } = useParams();
    const notificationApi = useNotificationApi();
    const { user } = useAuth();
    const [form] = Form.useForm();
    const [entryForm] = Form.useForm();

    // ------------------------------------------------------------
    // EDIT PERMISSION
    // ------------------------------------------------------------
    // Customers are read-only. Everyone else can edit.
    const canEditCR = useMemo(() => {
        if (!user) return false;
        const roleName = (user.roleName || '').toLowerCase();
        const userType = (user.userType || user.user_type || '').toLowerCase();
        return !(roleName.includes('customer') || userType === 'customer');
    }, [user]);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [notFound, setNotFound] = useState(false);

    // CR data
    const [cr, setCr] = useState(null);
    const [linkedTickets, setLinkedTickets] = useState([]);
    const [timeEntries, setTimeEntries] = useState([]);

    // Related data
    const [contracts, setContracts] = useState([]);
    const [ticketTypes, setTicketTypes] = useState([]);
    const [ticketTypesLoading, setTicketTypesLoading] = useState(false);
    const [availableTickets, setAvailableTickets] = useState([]);
    const [ticketsLoading, setTicketsLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);

    // Editing
    const [editingEntryKey, setEditingEntryKey] = useState(null);
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [linkingInProgress, setLinkingInProgress] = useState(false);
    const [ticketIdsToLink, setTicketIdsToLink] = useState([]);

    // Watch entry form for duration calculation
    const eStartHour = Form.useWatch('startHour', entryForm);
    const eStartMinute = Form.useWatch('startMinute', entryForm);
    const eStartAmPm = Form.useWatch('startAmPm', entryForm);
    const eEndHour = Form.useWatch('endHour', entryForm);
    const eEndMinute = Form.useWatch('endMinute', entryForm);
    const eEndAmPm = Form.useWatch('endAmPm', entryForm);

    // Notifications
    const showError = useCallback((title, content) => {
        notificationApi.error({
            message: title, description: content,
            duration: 4.5, placement: 'topRight', style: { width: 420 }
        });
    }, [notificationApi]);

    const showSuccess = useCallback((title, content) => {
        notificationApi.success({
            message: title, description: content,
            duration: 3, placement: 'topRight', style: { width: 420 }
        });
    }, [notificationApi]);

    // ------------------------------------------------------------
    // Load CR data
    // ------------------------------------------------------------
    const loadCR = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getCRById(crId);
            if (res.success && res.data) {
                const data = res.data;
                setCr(data);
                setLinkedTickets(data.linkedTickets || []);
                setTimeEntries((data.timeEntries || []).map(e => ({
                    ...e,
                    key: `srv-${e.id}`,
                    resources: e.resource
                        ? e.resource.split(',').map(r => r.trim()).filter(Boolean)
                        : []
                })));

                form.setFieldsValue({
                    contractId: data.contractId,
                    crTypeId: data.crTypeId || undefined,
                    requestDate: data.requestDate ? dayjs(data.requestDate) : null,
                    title: data.title || '',
                    description: data.description || '',
                    targetProductionDate: data.targetProductionDate
                        ? dayjs(data.targetProductionDate)
                        : null,
                    estimatedMandays: data.estimatedMandays != null
                        ? parseFloat(data.estimatedMandays)
                        : null,
                    userStoryNo: data.userStoryNo || '',
                    impactProgress: data.impactProgress || ''
                });
            } else {
                setNotFound(true);
            }
        } catch (err) {
            console.error('[CRDetailPage] Load failed:', err);
            if (err.response?.status === 404) {
                setNotFound(true);
            } else {
                showError('Failed to Load', err.response?.data?.message || 'Could not load CR.');
            }
        } finally {
            setLoading(false);
        }
    }, [crId, form, showError]);

    // ------------------------------------------------------------
    // Load supporting data
    // ------------------------------------------------------------
    useEffect(() => {
        loadCR();
    }, [loadCR]);

    useEffect(() => {
        const fetchContracts = async () => {
            try {
                const res = await api.get('/api/contracts');
                if (res.data.success) setContracts(res.data.data || []);
            } catch (err) {
                console.error('[CRDetailPage] Contracts fetch failed:', err);
            }
        };
        fetchContracts();
    }, []);

    useEffect(() => {
        const fetchTicketTypes = async () => {
            setTicketTypesLoading(true);
            try {
                const res = await api.get('/api/ticket-types');
                const list = res.data?.data || res.data || [];
                setTicketTypes(list);
            } catch (err) {
                console.error('[CRDetailPage] Ticket types fetch failed:', err);
            } finally {
                setTicketTypesLoading(false);
            }
        };
        fetchTicketTypes();
    }, []);

    // Only fetch users if canEdit (avoid unnecessary calls for customers)
    useEffect(() => {
        if (!canEditCR) return;
        const fetchUsers = async () => {
            setUsersLoading(true);
            try {
                const res = await api.get('/api/users/active-internal-and-partner');
                if (res.data.success) {
                    setUsers((res.data.data || []).filter(u => u.userType !== 'customer'));
                }
            } catch (err) {
                console.error('[CRDetailPage] Users fetch failed:', err);
            } finally {
                setUsersLoading(false);
            }
        };
        fetchUsers();
    }, [canEditCR]);

    // Available tickets (scoped to the CR's contract) — only if canEdit
    useEffect(() => {
        if (!canEditCR) return;
        const fetchTickets = async () => {
            if (!cr?.contractId) return;
            setTicketsLoading(true);
            try {
                const res = await getAvailableTickets(cr.contractId);
                if (res.success) setAvailableTickets(res.data || []);
            } catch (err) {
                console.error('[CRDetailPage] Tickets fetch failed:', err);
            } finally {
                setTicketsLoading(false);
            }
        };
        fetchTickets();
    }, [cr?.contractId, canEditCR]);

    // Auto-calculate duration in entry form
    useEffect(() => {
        if (!canEditCR) return;
        const allStart = eStartHour != null && eStartMinute != null && eStartAmPm;
        const allEnd = eEndHour != null && eEndMinute != null && eEndAmPm;
        if (allStart && allEnd) {
            let sh = parseInt(eStartHour);
            let eh = parseInt(eEndHour);
            if (eStartAmPm === 'PM' && sh !== 12) sh += 12;
            if (eStartAmPm === 'AM' && sh === 12) sh = 0;
            if (eEndAmPm === 'PM' && eh !== 12) eh += 12;
            if (eEndAmPm === 'AM' && eh === 12) eh = 0;
            const diff = (eh * 60 + parseInt(eEndMinute)) - (sh * 60 + parseInt(eStartMinute));
            if (diff > 0) {
                const h = Math.floor(diff / 60);
                const m = diff % 60;
                entryForm.setFieldsValue({ duration: parseFloat((h + m / 60).toFixed(2)) });
            } else {
                entryForm.setFieldsValue({ duration: null });
            }
        } else {
            entryForm.setFieldsValue({ duration: null });
        }
    }, [eStartHour, eStartMinute, eStartAmPm, eEndHour, eEndMinute, eEndAmPm, entryForm, canEditCR]);

    // ------------------------------------------------------------
    // Save CR metadata
    // ------------------------------------------------------------
    const handleSaveMetadata = async () => {
        if (!canEditCR) return;
        let values;
        try {
            values = await form.validateFields();
        } catch {
            showError('Missing Fields', 'Please fill in all required fields.');
            return;
        }

        setSaving(true);
        try {
            const res = await updateCR(crId, {
                crTypeId: values.crTypeId ? parseInt(values.crTypeId) : undefined,
                title: values.title?.trim(),
                requestDate: values.requestDate.format('YYYY-MM-DD'),
                description: values.description?.trim(),
                targetProductionDate: values.targetProductionDate
                    ? values.targetProductionDate.format('YYYY-MM-DD')
                    : null,
                estimatedMandays: values.estimatedMandays != null ? values.estimatedMandays : null,
                impactProgress: values.impactProgress?.trim() || null,
                userStoryNo: values.userStoryNo?.trim() || null
            });

            if (res.success) {
                showSuccess('Saved', 'CR details updated successfully.');
                setCr(prev => ({ ...prev, ...values }));
            } else {
                showError('Save Failed', res.message || 'Failed to update CR.');
            }
        } catch (err) {
            console.error('[CRDetailPage] Save failed:', err);
            showError('Save Failed', err.response?.data?.message || 'An error occurred.');
        } finally {
            setSaving(false);
        }
    };

    // ------------------------------------------------------------
    // Delete CR
    // ------------------------------------------------------------
    const handleDeleteCR = () => {
        if (!canEditCR) return;
        confirm({
            title: 'Delete this CR?',
            icon: <ExclamationCircleOutlined style={{ color: '#ef4444' }} />,
            content: 'This action cannot be undone. The CR can only be deleted if it has no linked tickets and no time entries.',
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    const res = await deleteCR(crId);
                    if (res.success) {
                        showSuccess('CR Deleted', 'The CR has been deleted.');
                        setTimeout(() => navigate('/cr-management'), 600);
                    } else {
                        showError('Delete Failed', res.message || 'Failed to delete CR.');
                    }
                } catch (err) {
                    console.error('[CRDetailPage] Delete failed:', err);
                    showError('Delete Failed', err.response?.data?.message || 'Could not delete CR.');
                }
            }
        });
    };

    // ------------------------------------------------------------
    // Link tickets
    // ------------------------------------------------------------
    const handleLinkTickets = async () => {
        if (!canEditCR) return;
        if (ticketIdsToLink.length === 0) return;
        setLinkingInProgress(true);
        try {
            const res = await linkTickets(crId, ticketIdsToLink);
            if (res.success) {
                showSuccess('Linked', res.message || 'Tickets linked.');
                setShowLinkModal(false);
                setTicketIdsToLink([]);
                await loadCR();
            } else {
                showError('Link Failed', res.message || 'Failed to link tickets.');
            }
        } catch (err) {
            console.error('[CRDetailPage] Link failed:', err);
            showError('Link Failed', err.response?.data?.message || 'An error occurred.');
        } finally {
            setLinkingInProgress(false);
        }
    };

    const handleUnlinkTicket = async (ticketId) => {
        if (!canEditCR) return;
        try {
            const res = await unlinkTicket(crId, ticketId);
            if (res.success) {
                showSuccess('Unlinked', 'Ticket unlinked.');
                await loadCR();
            } else {
                showError('Unlink Failed', res.message || 'Failed to unlink.');
            }
        } catch (err) {
            console.error('[CRDetailPage] Unlink failed:', err);
            showError('Unlink Failed', err.response?.data?.message || 'An error occurred.');
        }
    };

    // ------------------------------------------------------------
    // Time entries
    // ------------------------------------------------------------
    const handleAddOrUpdateEntry = async () => {
        if (!canEditCR) return;
        let values;
        try {
            values = await entryForm.validateFields();
        } catch {
            return;
        }

        const payload = {
            entryDate: values.entryDate.format('YYYY-MM-DD'),
            startTime: buildTimeString(values.startHour, values.startMinute, values.startAmPm),
            endTime: buildTimeString(values.endHour, values.endMinute, values.endAmPm),
            duration: parseFloat(values.duration),
            activityType: values.activityType,
            resource: (values.resources || []).join(', '),
            description: values.description || null
        };

        try {
            if (editingEntryKey) {
                const entry = timeEntries.find(e => e.key === editingEntryKey);
                if (!entry || !entry.id) {
                    showError('Save Failed', 'Entry not found.');
                    return;
                }
                const res = await updateTimeEntry(crId, entry.id, payload);
                if (res.success) {
                    showSuccess('Updated', 'Time entry updated.');
                } else {
                    showError('Update Failed', res.message || 'Failed to update.');
                    return;
                }
            } else {
                const res = await addTimeEntry(crId, payload);
                if (res.success) {
                    showSuccess('Added', 'Time entry added.');
                } else {
                    showError('Add Failed', res.message || 'Failed to add.');
                    return;
                }
            }

            entryForm.resetFields();
            entryForm.setFieldsValue(emptyEntryForm());
            setEditingEntryKey(null);
            await loadCR();
        } catch (err) {
            console.error('[CRDetailPage] Entry save failed:', err);
            showError('Save Failed', err.response?.data?.message || 'An error occurred.');
        }
    };

    const handleEditEntry = (record) => {
        if (!canEditCR) return;
        setEditingEntryKey(record.key);
        const start = parseTimeString(record.startTime);
        const end = parseTimeString(record.endTime);
        entryForm.setFieldsValue({
            entryDate: record.entryDate ? dayjs(record.entryDate) : null,
            activityType: record.activityType,
            startHour: start.hour,
            startMinute: start.minute,
            startAmPm: start.ampm,
            endHour: end.hour,
            endMinute: end.minute,
            endAmPm: end.ampm,
            duration: record.duration != null ? parseFloat(record.duration) : null,
            resources: record.resources || [],
            description: record.description || ''
        });
        document.querySelector('.ccr-entry-form-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleCancelEdit = () => {
        setEditingEntryKey(null);
        entryForm.resetFields();
        entryForm.setFieldsValue(emptyEntryForm());
    };

    const handleDeleteEntry = async (record) => {
        if (!canEditCR) return;
        try {
            const res = await deleteTimeEntry(crId, record.id);
            if (res.success) {
                showSuccess('Deleted', 'Time entry removed.');
                if (editingEntryKey === record.key) handleCancelEdit();
                await loadCR();
            } else {
                showError('Delete Failed', res.message || 'Failed to delete.');
            }
        } catch (err) {
            console.error('[CRDetailPage] Delete entry failed:', err);
            showError('Delete Failed', err.response?.data?.message || 'An error occurred.');
        }
    };

    // ------------------------------------------------------------
    // Totals
    // ------------------------------------------------------------
    const totals = useMemo(() => {
        let hours = 0;
        timeEntries.forEach(e => { hours += parseFloat(e.duration || 0); });
        return {
            totalHours: parseFloat(hours.toFixed(2)),
            totalManDays: parseFloat((hours / 8).toFixed(2))
        };
    }, [timeEntries]);

    // ------------------------------------------------------------
    // Ticket picker options (exclude already-linked)
    // ------------------------------------------------------------
    const ticketOptions = useMemo(() => {
        const linkedSet = new Set(linkedTickets.map(t => t.id));
        return availableTickets
            .filter(t => !linkedSet.has(t.id))
            .map(t => ({ value: t.id, ticket: t }));
    }, [availableTickets, linkedTickets]);

    // ------------------------------------------------------------
    // Table columns — linked tickets
    // ------------------------------------------------------------
    const linkColumns = useMemo(() => {
        const cols = [
            {
                title: 'Ticket UUID',
                dataIndex: 'ticketUuid',
                key: 'ticketUuid',
                width: 220,
                render: (text, record) => (
                    <a
                        className="crd-ticket-link"
                        onClick={() => navigate(`/tickets/${record.id}`)}
                    >
                        {text}
                    </a>
                )
            },
            {
                title: 'Title',
                dataIndex: 'title',
                key: 'title',
                ellipsis: { showTitle: false },
                width: 320,
                render: (t) => t ? <Tooltip title={t}><span>{t}</span></Tooltip> : '—'
            },
            {
                title: 'Type',
                dataIndex: 'ticketTypeName',
                key: 'ticketTypeName',
                width: 150,
                render: (t) => t ? <Tag color="blue">{t}</Tag> : '—'
            },
            {
                title: 'Link Type',
                dataIndex: 'linkType',
                key: 'linkType',
                width: 110,
                render: (t) => (
                    <Tag color={t === 'primary' ? 'green' : 'default'}>
                        {t === 'primary' ? 'Primary' : 'Related'}
                    </Tag>
                )
            }
        ];

        // Only add the Unlink action column if the user can edit
        if (canEditCR) {
            cols.push({
                title: '',
                key: 'actions',
                width: 120,
                align: 'center',
                render: (_, record) => (
                    <Popconfirm
                        title="Unlink this ticket?"
                        description="The ticket will remain, but will no longer be associated with this CR."
                        onConfirm={() => handleUnlinkTicket(record.id)}
                        okText="Unlink"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            style={{ padding: '0 6px' }}
                        >
                            Unlink
                        </Button>
                    </Popconfirm>
                )
            });
        }

        return cols;
    }, [canEditCR]);

    // ------------------------------------------------------------
    // Table columns — time entries
    // ------------------------------------------------------------
    const entryColumns = useMemo(() => {
        const cols = [
            {
                title: 'Date',
                dataIndex: 'entryDate',
                key: 'entryDate',
                width: 120,
                render: (d) => d ? dayjs(d).format('DD MMM YYYY') : '—'
            },
            {
                title: 'Activity Type',
                dataIndex: 'activityType',
                key: 'activityType',
                width: 170,
                render: (t) => t ? <Tag color="cyan">{t}</Tag> : '—'
            },
            {
                title: 'Start',
                dataIndex: 'startTime',
                key: 'startTime',
                width: 100,
                render: (t) => t ? dayjs(t, 'HH:mm:ss').format('hh:mm A') : '—'
            },
            {
                title: 'End',
                dataIndex: 'endTime',
                key: 'endTime',
                width: 100,
                render: (t) => t ? dayjs(t, 'HH:mm:ss').format('hh:mm A') : '—'
            },
            {
                title: 'Duration',
                dataIndex: 'duration',
                key: 'duration',
                width: 100,
                align: 'right',
                render: (v) => v != null ? <Text strong>{parseFloat(v).toFixed(2)} hrs</Text> : '—'
            },
            {
                title: 'Resource(s)',
                dataIndex: 'resources',
                key: 'resources',
                render: (rs) => {
                    if (!rs || rs.length === 0) return '—';
                    return (
                        <Space size={4} wrap>
                            {rs.slice(0, 2).map((r, i) => <Tag key={i} color="blue">{r}</Tag>)}
                            {rs.length > 2 && <Tag>+{rs.length - 2}</Tag>}
                        </Space>
                    );
                }
            },
            {
                title: 'Description',
                dataIndex: 'description',
                key: 'description',
                ellipsis: true,
                render: (t) => t || '—'
            }
        ];

        // Only add the Edit/Delete action column if the user can edit
        if (canEditCR) {
            cols.push({
                title: '',
                key: 'actions',
                width: 90,
                align: 'center',
                fixed: 'right',
                render: (_, record) => (
                    <Space size={2}>
                        <Button
                            type="text" size="small" icon={<EditOutlined />}
                            onClick={() => handleEditEntry(record)}
                        />
                        <Popconfirm
                            title="Delete this entry?"
                            onConfirm={() => handleDeleteEntry(record)}
                            okText="Delete"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true }}
                        >
                            <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                        </Popconfirm>
                    </Space>
                )
            });
        }

        return cols;
    }, [canEditCR, timeEntries, editingEntryKey]);

    // ------------------------------------------------------------
    // Loading / not found
    // ------------------------------------------------------------
    if (loading) {
        return (
            <div className="ccr-page">
                <Card bordered={false} style={{ borderRadius: 12 }}>
                    <Skeleton active paragraph={{ rows: 8 }} />
                </Card>
            </div>
        );
    }

    if (notFound) {
        return (
            <div className="ccr-page">
                <Card bordered={false} style={{ borderRadius: 12 }}>
                    <Result
                        status="404"
                        title="CR not found"
                        subTitle="The Change Request you're looking for doesn't exist or was deleted."
                        extra={
                            <Button type="primary" onClick={() => navigate('/cr-management')}>
                                Back to CR List
                            </Button>
                        }
                    />
                </Card>
            </div>
        );
    }

    // ------------------------------------------------------------
    // Render
    // ------------------------------------------------------------
    return (
        <div className="ccr-page">
            {/* Header */}
            <div className="ccr-header">
                <div className="ccr-header-left">
                    <div className="crd-cr-badge">
                        <FileTextOutlined className="crd-cr-badge-icon" />
                        <span className="crd-cr-number">{cr?.crNumber}</span>
                        <Tooltip title="Copy CR number">
                            <Button
                                type="text"
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={() => {
                                    navigator.clipboard.writeText(cr?.crNumber || '');
                                    showSuccess('Copied', 'CR number copied to clipboard.');
                                }}
                            />
                        </Tooltip>
                    </div>
                    <Title level={3} className="crd-cr-title">
                        {cr?.title || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>(no title)</span>}
                    </Title>
                    <div className="crd-cr-meta">
                        <Text type="secondary">
                            {cr?.contractName || 'Contract'}
                            {cr?.requestDate && ` • Requested ${dayjs(cr.requestDate).format('DD MMM YYYY')}`}
                        </Text>
                    </div>
                </div>
                <Space>
                    <Button
                        icon={<RollbackOutlined />}
                        size="large"
                        className="ccr-back-btn"
                        onClick={() => navigate('/cr-management')}
                    >
                        Back to CR List
                    </Button>
                    {canEditCR && (
                        <Button
                            danger
                            size="large"
                            icon={<DeleteOutlined />}
                            onClick={handleDeleteCR}
                        >
                            Delete CR
                        </Button>
                    )}
                </Space>
            </div>

            <Form form={form} layout="vertical" name="cr_detail_form" autoComplete="off">
                {/* SECTION 1 — DETAILS */}
                <Card className="ccr-section-card" bordered={false}>
                    <div className="ccr-section-header">
                        <FileTextOutlined className="ccr-section-icon" />
                        <div>
                            <div className="ccr-section-title">CR Details</div>
                            <div className="ccr-section-hint">
                                {canEditCR
                                    ? 'Edit the CR metadata and click "Save Changes" below.'
                                    : 'View-only. Contact an administrator to make changes.'}
                            </div>
                        </div>
                    </div>

                    <Row gutter={20}>
                        <Col xs={24} md={12}>
                            <Form.Item label="Contract">
                                <Input
                                    value={cr?.contractName || '—'}
                                    size="large"
                                    disabled
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="crTypeId"
                                label="Service Category"
                                rules={[{ required: true, message: 'Please select a service category' }]}
                                tooltip="Which type of service does this CR represent?"
                            >
                                <Select
                                    placeholder="Select service category"
                                    showSearch
                                    optionFilterProp="children"
                                    loading={ticketTypesLoading}
                                    size="large"
                                    disabled={!canEditCR}
                                >
                                    {ticketTypes.map(tt => (
                                        <Option key={tt.id} value={tt.id}>
                                            {tt.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="title" label="Title"
                        rules={[
                            { required: true, message: 'Please enter a title' },
                            { max: 255, message: 'Maximum 255 characters' }
                        ]}
                    >
                        <Input
                            placeholder="e.g., Notis Pink 2.0 — Add TARIKH_DOKUMEN field"
                            size="large"
                            maxLength={255}
                            showCount={{ formatter: (info) => `${info.value.length} / 255` }}
                            disabled={!canEditCR}
                        />
                    </Form.Item>

                    <Form.Item
                        name="description" label="Description"
                        rules={[{ required: true, message: 'Please enter a description' }]}
                    >
                        <TextArea
                            rows={4}
                            placeholder="Describe the change request..."
                            maxLength={2000}
                            showCount
                            disabled={!canEditCR}
                        />
                    </Form.Item>

                    <Row gutter={20}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="requestDate"
                                label="Request Date"
                                rules={[{ required: true, message: 'Please select a request date' }]}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format={DATE_FORMAT}
                                    size="large"
                                    disabled={!canEditCR}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="targetProductionDate" label="Target Production Date">
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format={DATE_FORMAT}
                                    size="large"
                                    disabled={!canEditCR}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={20}>
                        <Col xs={24} md={12}>
                            <Form.Item name="estimatedMandays" label="Estimated Man-Days">
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={0}
                                    step={0.5}
                                    size="large"
                                    placeholder="e.g., 15.5"
                                    disabled={!canEditCR}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="userStoryNo" label="User Story No.">
                                <Input
                                    placeholder="e.g., US-1234"
                                    size="large"
                                    maxLength={100}
                                    disabled={!canEditCR}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="impactProgress" label="Impact / Progress">
                        <TextArea
                            rows={3}
                            placeholder="e.g., URS received, In progress..."
                            maxLength={500}
                            disabled={!canEditCR}
                        />
                    </Form.Item>

                    {canEditCR && (
                        <div className="ccr-entry-form-actions">
                            <Button
                                type="primary"
                                size="large"
                                icon={<SaveOutlined />}
                                onClick={handleSaveMetadata}
                                loading={saving}
                                className="ccr-submit-btn"
                            >
                                Save Changes
                            </Button>
                        </div>
                    )}
                </Card>
            </Form>

            {/* SECTION 2 — LINKED TICKETS */}
            <Card className="ccr-section-card" bordered={false}>
                <div className="ccr-section-header">
                    <LinkOutlined className="ccr-section-icon" />
                    <div>
                        <div className="ccr-section-title">
                            Linked Tickets ({linkedTickets.length})
                        </div>
                        <div className="ccr-section-hint">
                            {canEditCR
                                ? 'Tickets that are part of this CR. Add or remove links here.'
                                : 'Tickets that are part of this CR.'}
                        </div>
                    </div>
                    {canEditCR && (
                        <div style={{ marginLeft: 'auto' }}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => {
                                    setTicketIdsToLink([]);
                                    setShowLinkModal(true);
                                }}
                            >
                                Link Tickets
                            </Button>
                        </div>
                    )}
                </div>

                {linkedTickets.length === 0 ? (
                    <Alert
                        type="info"
                        showIcon
                        message="No tickets linked yet"
                        description={
                            <span>
                                {canEditCR
                                    ? 'Click "Link Tickets" above to associate one or more tickets with this CR.'
                                    : 'No tickets have been linked to this CR yet.'}
                            </span>
                        }
                    />
                ) : (
                    <Table
                        columns={linkColumns}
                        dataSource={linkedTickets}
                        rowKey="id"
                        pagination={false}
                        size="small"
                        bordered
                        scroll={{ x: 'max-content' }}
                        className="ccr-entries-table"
                    />
                )}
            </Card>

            {/* SECTION 3 — TIME ENTRIES */}
            <Card className="ccr-section-card" bordered={false}>
                <div className="ccr-section-header">
                    <ClockCircleOutlined className="ccr-section-icon" />
                    <div>
                        <div className="ccr-section-title">Time Entries</div>
                        <div className="ccr-section-hint">
                            {canEditCR
                                ? 'Log time entries against this CR. Changes save immediately.'
                                : 'Time entries logged against this CR.'}
                        </div>
                    </div>
                </div>

                {/* Add / Edit form — only shown to editors */}
                {canEditCR && (
                    <Card
                        className={`ccr-entry-form-card ${editingEntryKey ? 'ccr-entry-form-editing' : ''}`}
                        bordered={false}
                    >
                        <div className="ccr-entry-form-title">
                            {editingEntryKey ? (
                                <><EditOutlined /> Editing entry</>
                            ) : (
                                <><PlusOutlined /> Add Time Entry</>
                            )}
                        </div>

                        <Form form={entryForm} layout="vertical" initialValues={emptyEntryForm()}>
                            <Row gutter={16}>
                                <Col xs={24} md={8}>
                                    <Form.Item name="entryDate" label="Date" rules={[{ required: true, message: 'Date is required' }]}>
                                        <DatePicker style={{ width: '100%' }} format={DATE_FORMAT} placeholder="Select date" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item name="activityType" label="Activity Type" rules={[{ required: true, message: 'Activity type is required' }]}>
                                        <Select placeholder="Select activity type" showSearch optionFilterProp="children">
                                            {ACTIVITY_TYPES.map(t => <Option key={t} value={t}>{t}</Option>)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item name="resources" label="Resource(s)" rules={[{ required: true, message: 'Select at least one resource' }]}>
                                        <Select
                                            mode="multiple"
                                            placeholder="Select one or more"
                                            loading={usersLoading}
                                            maxTagCount="responsive"
                                            showSearch
                                            filterOption={(input, option) =>
                                                (option?.children || '').toString().toLowerCase().includes(input.toLowerCase())
                                            }
                                        >
                                            {users.map(u => (
                                                <Option key={u.id} value={`${u.firstName} ${u.lastName}`}>
                                                    {u.firstName} {u.lastName}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col xs={24} md={8}>
                                    <Form.Item label="Start Time" required style={{ marginBottom: 0 }}>
                                        <div className="ccr-time-picker">
                                            <Form.Item name="startHour" noStyle rules={[{ required: true, message: 'HH' }]}>
                                                <Select placeholder="HH" style={{ width: 68 }}>
                                                    {HOURS.map(h => <Option key={h} value={h}>{String(h).padStart(2, '0')}</Option>)}
                                                </Select>
                                            </Form.Item>
                                            <span className="ccr-time-sep">:</span>
                                            <Form.Item name="startMinute" noStyle rules={[{ required: true, message: 'MM' }]}>
                                                <Select placeholder="MM" style={{ width: 68 }}>
                                                    {MINUTES.map(m => <Option key={m} value={m}>{String(m).padStart(2, '0')}</Option>)}
                                                </Select>
                                            </Form.Item>
                                            <Form.Item name="startAmPm" noStyle rules={[{ required: true, message: 'AM/PM' }]}>
                                                <Select placeholder="AM/PM" style={{ width: 85 }}>
                                                    {AMPM.map(p => <Option key={p} value={p}>{p}</Option>)}
                                                </Select>
                                            </Form.Item>
                                        </div>
                                    </Form.Item>
                                    <Form.Item name="startHour" hidden rules={[{ required: true, message: 'Start time is required' }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item label="End Time" required style={{ marginBottom: 0 }}>
                                        <div className="ccr-time-picker">
                                            <Form.Item name="endHour" noStyle rules={[{ required: true, message: 'HH' }]}>
                                                <Select placeholder="HH" style={{ width: 68 }}>
                                                    {HOURS.map(h => <Option key={h} value={h}>{String(h).padStart(2, '0')}</Option>)}
                                                </Select>
                                            </Form.Item>
                                            <span className="ccr-time-sep">:</span>
                                            <Form.Item name="endMinute" noStyle rules={[{ required: true, message: 'MM' }]}>
                                                <Select placeholder="MM" style={{ width: 68 }}>
                                                    {MINUTES.map(m => <Option key={m} value={m}>{String(m).padStart(2, '0')}</Option>)}
                                                </Select>
                                            </Form.Item>
                                            <Form.Item name="endAmPm" noStyle rules={[{ required: true, message: 'AM/PM' }]}>
                                                <Select placeholder="AM/PM" style={{ width: 85 }}>
                                                    {AMPM.map(p => <Option key={p} value={p}>{p}</Option>)}
                                                </Select>
                                            </Form.Item>
                                        </div>
                                    </Form.Item>
                                    <Form.Item name="endHour" hidden rules={[{ required: true, message: 'End time is required' }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item name="duration" label="Duration (hours)" rules={[{ required: true, message: 'Duration is required' }]}>
                                        <InputNumber style={{ width: '100%' }} disabled placeholder="Auto-calculated" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item name="description" label="Description">
                                <TextArea rows={2} placeholder="What was done..." maxLength={500} />
                            </Form.Item>

                            <div className="ccr-entry-form-actions">
                                {editingEntryKey && (
                                    <Button icon={<CloseOutlined />} onClick={handleCancelEdit}>
                                        Cancel Edit
                                    </Button>
                                )}
                                <Button
                                    type="primary"
                                    icon={editingEntryKey ? <CheckOutlined /> : <PlusOutlined />}
                                    onClick={handleAddOrUpdateEntry}
                                >
                                    {editingEntryKey ? 'Update Entry' : 'Add Entry'}
                                </Button>
                            </div>
                        </Form>
                    </Card>
                )}

                {/* Entries list */}
                {timeEntries.length > 0 && (
                    <>
                        <div className="ccr-entries-list-header">
                            <Text strong style={{ fontSize: 14 }}>
                                Entries ({timeEntries.length})
                            </Text>
                        </div>

                        <Table
                            columns={entryColumns}
                            dataSource={timeEntries}
                            rowKey="key"
                            pagination={false}
                            size="small"
                            bordered
                            scroll={{ x: 'max-content' }}
                            className="ccr-entries-table"
                        />

                        <div className="ccr-totals-panel">
                            <div className="ccr-totals-item">
                                <span className="ccr-totals-label">Total Hours</span>
                                <span className="ccr-totals-value ccr-totals-hours">
                                    {totals.totalHours.toFixed(2)} hrs
                                </span>
                            </div>
                            <div className="ccr-totals-divider" />
                            <div className="ccr-totals-item">
                                <span className="ccr-totals-label">Total Man-Days</span>
                                <span className="ccr-totals-value ccr-totals-days">
                                    {totals.totalManDays.toFixed(2)} days
                                </span>
                            </div>
                            <div className="ccr-totals-note">(8 hours = 1 day)</div>
                        </div>
                    </>
                )}
            </Card>

            {/* Link Tickets modal — only mounted when canEdit */}
            {canEditCR && (
                <Modal
                    title="Link Tickets"
                    open={showLinkModal}
                    onCancel={() => {
                        setShowLinkModal(false);
                        setTicketIdsToLink([]);
                    }}
                    onOk={handleLinkTickets}
                    okText="Link Selected"
                    cancelText="Cancel"
                    okButtonProps={{ disabled: ticketIdsToLink.length === 0, loading: linkingInProgress }}
                    width={680}
                >
                    <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                        Select tickets to link to this CR. Only tickets from the same contract are shown.
                    </Text>
                    <Select
                        mode="multiple"
                        placeholder="Search and select tickets..."
                        style={{ width: '100%' }}
                        size="large"
                        loading={ticketsLoading}
                        value={ticketIdsToLink}
                        onChange={setTicketIdsToLink}
                        showSearch
                        filterOption={(input, option) => {
                            const t = ticketOptions.find(o => o.value === option.value)?.ticket;
                            if (!t) return false;
                            const s = input.toLowerCase();
                            return (
                                (t.ticketUuid || '').toLowerCase().includes(s) ||
                                (t.title || '').toLowerCase().includes(s) ||
                                (t.ticketTypeName || '').toLowerCase().includes(s)
                            );
                        }}
                        maxTagCount="responsive"
                        notFoundContent={ticketsLoading ? <Spin size="small" /> : 'No tickets available'}
                    >
                        {ticketOptions.map(opt => (
                            <Option key={opt.value} value={opt.value}>
                                <div className="ccr-ticket-option">
                                    <span className="ccr-ticket-option-uuid">{opt.ticket.ticketUuid}</span>
                                    {opt.ticket.ticketTypeName && (
                                        <Tag color="blue" className="ccr-ticket-option-type">
                                            {opt.ticket.ticketTypeName}
                                        </Tag>
                                    )}
                                    <span className="ccr-ticket-option-title">{opt.ticket.title}</span>
                                </div>
                            </Option>
                        ))}
                    </Select>
                </Modal>
            )}
        </div>
    );
};

export default CRDetailPage;